import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../tps.css';

const SubmissionList = () => {
  const platformID = localStorage.getItem('platformID');
  // console.log('Sending notify with platformID:', platformID);

  const API = process.env.REACT_APP_API_BASE_URL;

  // console.log(API);

  const location = useLocation();

  // Default filters from navigation state or fallback
  const defaultStatusFilter = location.state?.defaultStatusFilter || 'all';
  const defaultTypeFilter = location.state?.defaultTypeFilter || 'all';
  const defaultScheduleFilter = location.state?.defaultScheduleFilter || 'all';

  const [statusFilter, setStatusFilter] = useState(defaultStatusFilter);
  const [typeFilter, setTypeFilter] = useState(defaultTypeFilter);
  const [scheduleFilter, setScheduleFilter] = useState(defaultScheduleFilter);

  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [page, setPage] = useState(1);
  const perPage = 10;

  // Track submissions notified timestamp (persisted in localStorage)
  const [notifiedSubmissions, setNotifiedSubmissions] = useState(() => {
    const saved = localStorage.getItem('notifiedSubmissions');
    return saved ? JSON.parse(saved) : {};
  });

  useEffect(() => {
    if (!platformID) return;

    axios
      .get(`${API}/api/subassignments/submissions-list/${platformID}`)
      .then(res => {
        setSubmissions(res.data);
        console.log(res.data);
        setPage(1);
      })
      .catch(err => {
        console.error("Error fetching submissions:", err);
      });
  }, [API, platformID]);

  useEffect(() => {
    let filtered = submissions;

    const status = statusFilter.toLowerCase();
    const type = typeFilter.toLowerCase();

    // STATUS FILTERING
    if (status === 'accept') {
      if (type === 'abstract') {
        filtered = filtered.map(sub =>
          sub.Status?.toLowerCase() === 'pending' && sub.IsReview === 'Reviewed'
            ? { ...sub, Status: 'Accept' }
            : sub
        );
      }
      filtered = filtered.filter(sub => sub.Status?.toLowerCase() === 'accept');
    } else if (status === 'pending') {
      const grouped = filtered.reduce((acc, sub) => {
        const key = sub.parentSubmissionID ?? sub.submissionID;
        acc[key] = acc[key] || [];
        acc[key].push(sub);
        return acc;
      }, {});

      filtered = Object.values(grouped).flatMap(group => {
        const hasRevision = group.some(s => /_Revision$/i.test(s.Title));
        if (hasRevision) {
          return group.filter(s => /_Revision$/i.test(s.Title) && s.Status?.toLowerCase() === 'pending');
        }

        const hasFull = group.some(s => /_Full_Paper$/i.test(s.Title));
        if (hasFull) {
          return group.filter(s => /_Full_Paper$/i.test(s.Title) && s.Status?.toLowerCase() === 'pending');
        }

        const hasAbstract = group.some(s => /_Abstract$/i.test(s.Title));
        if (hasAbstract) {
          return group.filter(s => /_Abstract$/i.test(s.Title) && s.Status?.toLowerCase() === 'pending');
        }

        return group.filter(s => s.Status?.toLowerCase() === 'pending');
      });
    } else if (status === 'reject') {
      filtered = filtered.filter(sub => sub.Status?.toLowerCase() === 'reject');
    }
    // else if status === 'all' no filtering by status

    // TYPE FILTERING
    if (type === 'abstract') {
      filtered = filtered.filter(sub => /_Abstract$/i.test(sub.Title));
    } else if (type === 'full') {
      filtered = filtered.filter(sub => /_Full_Paper$/i.test(sub.Title));
    } else if (type === 'revision') {
      filtered = filtered.filter(sub => /_Revision$/i.test(sub.Title));
    }
    // 'all' means no filtering by paper type

    // ✅ Schedule/Hold FILTERING
    if (scheduleFilter === 'scheduled') {
      filtered = filtered.filter(sub => sub.Schedule === 1);
    } else if (scheduleFilter === 'hold') {
      filtered = filtered.filter(sub => sub.Hold === 1);
    }

    setFilteredSubmissions(filtered);
    setPage(1);
  }, [submissions, statusFilter, typeFilter, scheduleFilter]); // ✅ Added scheduleFilter dependency

  const paginatedSubmissions = filteredSubmissions.slice((page - 1) * perPage, page * perPage);

  // Export to PDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Submissions Report', 14, 22);

    autoTable(doc, {
      startY: 30,
      head: [['Title', 'Author', 'Status', 'Date', 'Schedule', 'Hold']],
      body: filteredSubmissions.map(sub => [
        sub.Title,
        sub.AuthorName || 'N/A',
        sub.Status,
        new Date(sub.CreatedAt).toLocaleDateString(),
        sub.Status?.toLowerCase() === 'accept' && /(_Full_Paper|_Revision)$/i.test(sub.Title) ? sub.Schedule || '—' : '—',
        sub.Status?.toLowerCase() === 'accept' && /(_Full_Paper|_Revision)$/i.test(sub.Title) ? sub.Hold || '—' : '—',
      ]),
      styles: { fontSize: 10, cellPadding: 2 },
      theme: 'grid',
    });

    doc.save('submissions_report.pdf');
  };

  // Export to Excel
  const exportToExcel = () => {
    const data = filteredSubmissions.map(sub => ({
      Title: sub.Title,
      Author: sub.AuthorName || 'N/A',
      Status: sub.Status,
      Date: new Date(sub.CreatedAt).toLocaleDateString(),
      Schedule: sub.Status?.toLowerCase() === 'accept' && /(_Full_Paper|_Revision)$/i.test(sub.Title) ? sub.Schedule || '—' : '—',
      Hold: sub.Status?.toLowerCase() === 'accept' && /(_Full_Paper|_Revision)$/i.test(sub.Title) ? sub.Hold || '—' : '—',
    }));

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Submissions');
    XLSX.writeFile(workbook, 'submissions_report.xlsx');
  };

  // Check if a submission is older than or equal to one month
  const isOlderThanOneMonth = (createdAt) => {
    const createdDate = new Date(createdAt);
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    return createdDate <= oneMonthAgo;
  };

  // Determine if we can show Notify button for a submission
  const canNotify = (submission) => {
    // Only allow parent submissions (e.g., Abstract)
    if (submission.parentSubmissionID !== null) return false;

    // Ensure it's accepted
    if (submission.Status?.toLowerCase() !== 'accept') return false;

    // Must be older than 1 month
    if (!isOlderThanOneMonth(submission.CreatedAt)) return false;

    // Don't allow notify if a child (e.g. Full Paper or Revision) exists
    const hasChild = submissions.some(s => s.parentSubmissionID === submission.submissionID);
    if (hasChild) return false;

    // Already notified recently?
    const notifiedAt = notifiedSubmissions[submission.submissionID];
    if (!notifiedAt) return true;

    const notifiedDate = new Date(notifiedAt);
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    return notifiedDate <= oneMonthAgo;
  };

  // Send notification and mark submission as notified
  const handleNotify = async (submission) => {
    try {
      await axios.post(`${API}/api/subassignments/notify-author`, {
        submissionID: submission.submissionID,
        email: submission.EmailID,
        name: submission.AuthorName,
        title: submission.Title,
        platformID: platformID,
      });
      alert(`Notification sent to ${submission.EmailID}`);

      // Mark as notified with current timestamp
      const newNotified = {
        ...notifiedSubmissions,
        [submission.submissionID]: new Date().toISOString(),
      };
      setNotifiedSubmissions(newNotified);
      localStorage.setItem('notifiedSubmissions', JSON.stringify(newNotified));
    } catch (err) {
      console.error('Error sending notification:', err);
      alert('Failed to send notification.');
    }
  };

  const toggleStatus = async (submission, type) => {
    // type = 'Schedule' or 'Hold'
    const newValue = submission[type] === 1 ? 0 : 1;

    // Determine the other type
    const otherType = type === 'Schedule' ? 'Hold' : 'Schedule';

    try {
      await axios.put(`${API}/api/reviewer-comments/update-status`, {
        submissionID: submission.submissionID,
        Status: type,
        value: newValue,
      });

      // Update local state
      setSubmissions(prev =>
        prev.map(sub => {
          if (sub.submissionID === submission.submissionID) {
            // If setting current type to 1, reset other to 0
            if (newValue === 1) {
              return { ...sub, [type]: newValue, [otherType]: 0 };
            } else {
              // Otherwise just toggle current type
              return { ...sub, [type]: newValue };
            }
          }
          return sub;
        })
      );

    } catch (err) {
      console.error(`Error updating ${type} status:`, err);
      alert(`Failed to update ${type} status.`);
    }
  };

  return (
    <div className="container my-4 ml-2">
      <h3 className="page-title mb-4">Paper Submissions</h3>

      {/* Filters */}
      <div className="d-flex flex-wrap gap-4 mb-3">
        {/* Status Filter */}
        <div>
          <strong>Status Filter:</strong>
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="accept">Accept</option>
            <option value="pending">Pending</option>
            <option value="reject">Reject</option>
          </select>
        </div>

        {/* Type Filter */}
        <div>
          <strong>Type Filter:</strong>
          <select
            className="form-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="abstract">Abstract</option>
            <option value="full">Full Paper</option>
            <option value="revision">Revision</option>
          </select>
        </div>

        {/* ✅ New Schedule Filter */}
        <div>
          <strong>Schedule Filter:</strong>
          <select
            className="form-select"
            value={scheduleFilter}
            onChange={(e) => setScheduleFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="scheduled">Scheduled</option>
            <option value="hold">Hold</option>
          </select>
        </div>

        {/* Export Buttons */}
        <div className="d-flex align-items-end gap-2">
          <button className="btn btn-primary" onClick={exportToPDF}>Export PDF</button>
          <button className="btn btn-success" onClick={exportToExcel}>Export Excel</button>
        </div>
      </div>

      {/* Submissions Table */}
      <table className="table table-bordered table-striped">
        <thead>
          <tr>
            <th>Title</th>
            <th>Author</th>
            <th>Status</th>
            <th>Date</th>
            <th>Schedule</th>
            <th>Hold</th>
            <th>Notify</th>
          </tr>
        </thead>
        <tbody>
          {paginatedSubmissions.length === 0 ? (
            <tr>
              <td colSpan="7" className="text-center">No submissions found.</td>
            </tr>
          ) : (
            paginatedSubmissions.map(sub => (
              <tr key={sub.submissionID}>
                <td>{sub.Title}</td>
                <td>{sub.AuthorName || 'N/A'}</td>
                <td>{sub.Status}</td>
                <td>{new Date(sub.CreatedAt).toLocaleDateString()}</td>
<td>
  {(() => {
    // console.log('-------------------------');
    // console.log('Title:', sub.Title);
    // console.log('Status:', sub.Status);
    // console.log('ParentSubmissionID:', sub.parentSubmissionID);
    // console.log('Schedule:', sub.Schedule);

    const isAccepted = sub.Status?.toLowerCase() === 'accept';
    const hasParent = !!sub.parentSubmissionID; // true if Full/Revision paper

    // ✅ Only show Schedule for accepted submissions that have a parent (Full Paper or Revision)
    if (isAccepted && hasParent) {
      return (
        <button
          className={`btn btn-sm ${sub.Schedule === 1 ? 'btn-success' : 'btn-outline-secondary'}`}
          onClick={() => toggleStatus(sub, 'Schedule')}
          title="Toggle Schedule"
        >
          {sub.Schedule === 1 ? 'Yes' : 'No'}
        </button>
      );
    } else {
      // console.log('⛔ Not showing Schedule button for this paper');
      return null;
    }
  })()}
</td>

<td>
  {(() => {
    const isAccepted = sub.Status?.toLowerCase() === 'accept';
    const hasParent = !!sub.parentSubmissionID;

    // ✅ Only show Hold for accepted submissions that have a parent
    if (isAccepted && hasParent) {
      return (
        <button
          className={`btn btn-sm ${sub.Hold === 1 ? 'btn-warning' : 'btn-outline-secondary'}`}
          onClick={() => toggleStatus(sub, 'Hold')}
          title="Toggle Hold"
        >
          {sub.Hold === 1 ? 'Yes' : 'No'}
        </button>
      );
    } else {
      return null;
    }
  })()}
</td>
                <td>
                  {canNotify(sub) ? (
                    <button className="btn btn-info btn-sm" onClick={() => handleNotify(sub)}>
                      Notify
                    </button>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {/* Pagination */}
      {filteredSubmissions.length > perPage && (
        <nav>
          <ul className="pagination">
            {Array.from({ length: Math.ceil(filteredSubmissions.length / perPage) }, (_, i) => (
              <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => setPage(i + 1)}>
                  {i + 1}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
};

export default SubmissionList;
